<template>
  <fragment>
    <app-header title="Project Dependencies" />

    <div class="container container-fluid my-4">
      <div
        tabindex="-1"
        role="dialog"
        class="modal modal-side-drawer-filters fade fixed-end op"
      >
        <div
          role="document"
          class="modal-dialog modal-dialog-vertical dw-md-8 dw-lg-6 dw-xl-5"
        >
          <div class="modal-content">
            <div class="modal-header">
              <h2 class="modal-title">Advanced Search</h2>
              <button
                type="button"
                data-bs-dismiss="modal"
                aria-label="Close"
                class="close"
              >
                <span aria-hidden="true"><i class="far fa-times"></i></span>
              </button>
            </div>
            <div class="modal-body">
              <div class="data-filters">
                <div class="row">
                  <div class="col-12 col-lg-5">
                    <label>Search</label>
                    <div class="input-group">
                      <span class="input-group-text">
                        <i class="fal fa-search"></i>
                      </span>
                      <input type="text" class="form-control" :value="filters.search" @input="updateSearch"/>
                    </div>
                  </div>
                  <div class="col-6 col-lg-4">
                    <label>SLA</label>
                    <div class="input-group">
                      <span class="input-group-text">
                        <i class="fal fa-handshake"></i>
                      </span>
                      <multiselect
                        label="label"
                        :options="slaOptions"
                        :show-labels="false"
                        :allow-empty="false"
                        placeholder
                        v-model="slaSelected"
                        track-by="value"
                      />
                    </div>
                  </div>
                  <div class="col-6 col-lg-3">
                    <label>Branch</label>
                    <div class="input-group">
                      <span class="input-group-text">
                        <i class="fal fa-code-branch"></i>
                      </span>

                      <multiselect
                        :options="branchOptions"
                        :show-labels="false"
                        :allow-empty="false"
                        placeholder
                        v-model="filters.branch"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ul class="list-data">
        <li class="data-heading d-none d-xl-block">
          <div class="row">
            <div class="col-4">
              <p>Project</p>
            </div>
            <div class="col-2">
              <p>Laravel</p>
            </div>
            <div class="col-2">
              <p>Vue</p>
            </div>
            <div class="col-2">
              <p>Dependencies</p>
            </div>
            <div class="col-2">
              <p>Vulnerabilities</p>
            </div>
          </div>
        </li>
        <li v-for="application in applications" :key="application.id">
          <router-link :to="{ name: 'support.project-dependencies.single', params: { applicationId: application.id } }">
            <div class="row">
              <div class="col-6 col-xl-4">
                <div class="row g-0">
                  <div class="col-auto d-flex align-items-center">
                    <span class="badge bg-primary me-1">{{ application.name }}</span>
                  </div>
                  <div class="col d-flex align-items-center">
                    <span class="strong" :title="application.company">{{ application.company }}</span>
                    <i v-if="application.yarn" class="fab fa-yarn fa-lg ms-1 text-secondary" v-tooltip.top="'Project uses Yarn'"></i>
                  </div>
                </div>
              </div>
              <div class="col-3 col-xl-2">
                <p>
                  <span class="data-label d-xl-none">Laravel</span>
                  <dependency-version
                    :current="application.current_laravel_version"
                    :latest="latestVersions.laravel"
                  />
                </p>
              </div>
              <div class="col-3 col-xl-2">
                <p>
                  <span class="data-label d-xl-none">Vue</span>
                  <dependency-version
                    :current="application.current_vue_version"
                    :latest="latestVersions.vue"
                  />
                </p>
              </div>
              <div
                class="col-6 col-xl-2 align-items-start align-items-xl-center"
              >
                <p class="d-flex flex-column align-items-start">
                  <span class="data-label d-xl-none">Dependencies</span>
                  <count-chip type="danger" suffix="major updates" :count="application.major_updates_count"/>
                  <count-chip type="warning" suffix="minor updates" :count="application.minor_updates_count"/>
                  <count-chip type="success" suffix="up to date" :count="application.up_to_date_count"/>
                </p>
              </div>
              <div
                class="col-6 col-xl-2 align-items-start align-items-xl-center"
              >
                <p class="d-flex flex-column align-items-start">
                  <span class="data-label d-xl-none">Vulnerabilities</span>

                  <count-chip type="dark" suffix="critical" :count="application.critical_vulnerabilities_count"/>
                  <count-chip type="danger" suffix="high" :count="application.high_vulnerabilities_count"/>
                  <count-chip type="warning" suffix="moderate" :count="application.moderate_vulnerabilities_count"/>
                  <count-chip type="light" suffix="low" :count="application.low_vulnerabilities_count"/>
                </p>
              </div>
            </div>
          </router-link>
        </li>
      </ul>
    </div>
  </fragment>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import { useQueryParamFilters } from '@codium/codium-vue'
import DependencyVersion from './DependencyVersion'
import CountChip from './CountChip'
import Multiselect from 'vue-multiselect'

export default {
  mixins: [useQueryParamFilters()],

  components: {
    DependencyVersion,
    CountChip,
    Multiselect
  },

  data () {
    return {
      filters: {
        branch: 'staging',
        sla: 'any',
        search: null
      },
      slaOptions: [
        { value: 'any', label: 'All clients' },
        { value: 'active', label: 'Only clients with active SLA' },
        { value: 'inactive', label: 'Only clients without active SLA' }
      ],
      branchOptions: ['staging', 'master']
    }
  },

  computed: {
    ...mapState('dependencyApplications', ['applications', 'latestVersions']),

    slaSelected: {
      get () {
        return this.slaOptions.find(({ value }) => value === this.filters.sla)
      },

      set ({ value }) {
        this.filters.sla = value
      }
    }
  },

  watch: {
    queryParams: {
      async handler (params) {
        await this.getApplications(params)
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    ...mapActions('dependencyApplications', ['getApplications']),

    updateSearch: debounce(function (event) {
      this.filters.search = event.target.value
    }, 750)
  }
}
</script>
